import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  DollarSign,
  Sparkles,
  CheckCircle,
  Loader2,
  Briefcase,
  Send,
  FileText,
  AlertCircle,
  Plus,
  Trash2,
  ShieldAlert,
  Coins,
  RefreshCw,
  FileCode,
  Zap,
  MessageSquare,
  Users,
  Calendar, 
  MapPin
} from 'lucide-react';
import { runAIGeneration } from '../services/geminiService';
import PaymentGateway from './PaymentGateway';
import UserSearch from './UserSearch';
import DualPaymentModal from './DualPaymentModal';
import {
  UserRole,
  CollabProposal,
  Milestone,
  SmartContractDraft,
  User,
  Transaction,
  ProposalStatus,
  Message,
} from '@/types';
import {
  checkContentForViolation,
  MODERATION_WARNING_TEXT,
} from '../services/moderationService';
import ContractEditor from './ContractEditor';
import { useToast } from '../contexts/ToastContext';
import { API_BASE_URL } from '../config';
import { useAuth } from '@/contexts/AuthContext';
import { cleanupAIResponse } from '@/lib/utils';
import ProposalProgressBar from './ProposalProgressBar';
import CollabNegotiationModal from './CollabNegotiationModal'; 
import { SubmitWorkModal } from './SubmitWorkModal';
import { WorkReviewModal } from './WorkReviewModal';

type ViewMode = 'edit' | 'diff' | 'chat';

interface BookingHubProps {
  onBlockUser: () => void;
  onOpenExchange?: () => void;
}

const MOCK_PROPOSALS: CollabProposal[] = [
  {
    id: 'p-urgent-1',
    eventName: 'Last Minute Warehouse Party',
    artistName: 'DJ Havoc',
    artistId: 'dj-havoc-id',
    proposerId: 'venue-promoter-id', 
    status: ProposalStatus.PENDING,
    budget: '1500',
    currency: 'USD',
    sentDate: new Date().toISOString(),
    paymentType: 'Lump Sum',
    isUrgent: true,
    receiverNote: 'Need a high-energy DJ for tonight!',
    contractData: {
      id: 'c-urgent-1',
      contractType: 'Service Agreement',
      content: '// URGENT DRAFT...',
      lastEditedBy: 'User',
      version: 1,
      status: 'Pending Review',
    },
    chatHistory: [],
    submittedWork: [],
  },
];

const BookingHub: React.FC<BookingHubProps> = ({ onBlockUser, onOpenExchange }) => {
  const { notify } = useToast();
  const { token, currentUser: user } = useAuth();
  const [activeTab, setActiveTab] = useState<'create' | 'manage'>('manage');
  const [isGenerating, setIsGenerating] = useState(false);
  const [description, setDescription] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [proposalToFund, setProposalToFund] = useState<CollabProposal | null>(null);

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [viewingContract, setViewingContract] = useState<SmartContractDraft | null>(null);
  const [currentProposalId, setCurrentProposalId] = useState<string>('');

  const [isDualPaymentModalOpen, setIsDualPaymentModalOpen] = useState(false);
  const [isSubmitWorkModalOpen, setIsSubmitWorkModalOpen] = useState(false);
  const [isWorkReviewModalOpen, setIsWorkReviewModalOpen] = useState(false);
  const [selectedProposalForWork, setSelectedProposalForWork] = useState<CollabProposal | null>(null);

  const [isNegotiationModalOpen, setIsNegotiationModalOpen] = useState(false);
  const [negotiationViewMode, setNegotiationViewMode] = useState<ViewMode>('edit');
  const [selectedProposalForNegotiation, setSelectedProposalForNegotiation] = useState<CollabProposal | null>(null);
  const [draftProposal, setDraftProposal] = useState<CollabProposal | null>(null);
  const [previousProposalForDiff, setPreviousProposalForDiff] = useState<CollabProposal | null>(null);

  const isNegotiationModalReadOnly = useMemo(() => {
      if (!selectedProposalForNegotiation) return false;
      return ![ProposalStatus.DRAFT, ProposalStatus.NEGOTIATION].includes(selectedProposalForNegotiation.status);
  }, [selectedProposalForNegotiation]);

  const [eventName, setEventName] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [genre, setGenre] = useState('');
  const [rate, setRate] = useState('');
  const [currency, setCurrency] = useState<'USD' | 'ETH' | 'KALA'>('USD');

  const [isUrgent, setIsUrgent] = useState(false);
  const [eventDate, setEventDate] = useState('');
  const [venue, setVenue] = useState('');
  const [receiverNote, setReceiverNote] = useState('');
  const [showDualPayment, setShowDualPayment] = useState(false);

  const [paymentType, setPaymentType] = useState<'Lump Sum' | 'Milestone Based'>('Lump Sum');
  const [milestones, setMilestones] = useState<Milestone[]>([
    { id: 'm1', task: 'Initial Deposit', percentage: 50, status: 'Pending', deadline: new Date().toISOString().split('T')[0] },
    { id: 'm2', task: 'Project Completion', percentage: 50, status: 'Pending', deadline: new Date().toISOString().split('T')[0] },
  ]);

  const [myProposals, setMyProposals] = useState<CollabProposal[]>([]);
  const [prevMyProposals, setPrevMyProposals] = useState<CollabProposal[]>([]);

  const [proposalUpdatePayload, setProposalUpdatePayload] = useState<{ id: string; updates: Partial<CollabProposal> } | null>(null);
  const [transactionForUpdate, setTransactionForUpdate] = useState<Transaction | null>(null);

  const fetchProposals = useCallback(async (isInitialFetch = false, signal: AbortSignal) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/proposals/collab`, {
        headers: { 'Authorization': `Bearer ${token}` },
        signal, // Pass the abort signal to the fetch request
      });

      if (!response.ok) throw new Error('Failed to fetch proposals.');

      const liveProposals = await response.json();

      setMyProposals(prevProposals => {
        setPrevMyProposals(prevProposals);

        if (liveProposals.length === 0) {
          if (isInitialFetch) notify('No proposals yet. Showing an example.', 'info');
          return MOCK_PROPOSALS;
        } 
        return liveProposals;
      });

    } catch (error) {
        if ((error as Error).name === 'AbortError') {
            console.log('Fetch aborted');
            return;
        }
      console.error(error);
      if (isInitialFetch) notify('Could not load proposals. Showing examples.', 'warn');
      setMyProposals(MOCK_PROPOSALS);
    }
  }, [token, notify]);

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    let pollingInterval: NodeJS.Timeout | null = null;

    if (activeTab === 'manage' && token && !isNegotiationModalOpen) {
      fetchProposals(true, signal);
      pollingInterval = setInterval(() => fetchProposals(false, signal), 10000);
    }

    return () => {
      abortController.abort(); // Abort the fetch request on cleanup
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [activeTab, token, fetchProposals, isNegotiationModalOpen]);

  const handleAIGenerate = async () => {
    if (!selectedUser || !eventName) {
      notify('Please provide a Proposal Name and select a Recipient first.', 'warn');
      return;
    }
    setIsGenerating(true);
    const prompt = `You are an expert contract assistant for a user named "${user?.name || 'The Proposer'}". The user is creating a proposal to HIRE a service provider, "${selectedUser.name}", for an event/project named "${eventName}".\n\nYour task is to generate a formal proposal draft from the user's perspective, REQUESTING the recipient's services. The draft should clearly outline the services being requested from the recipient.\n\nKeep the entire response under 1000 characters. Include clear terms, conditions, and placeholders for the user to fill in. Use placeholders like [INSERT DATE], [DESCRIBE RECIPIENT'S TASKS], [SPECIFY PAYMENT TERMS FOR RECIPIENT], and [OUTLINE DELIVERABLES FROM RECIPIENT]. The tone should be professional and clear.`;
    try {
      const rawDesc = await runAIGeneration(prompt, token);
      const cleanedDesc = cleanupAIResponse(rawDesc);
      setDescription(cleanedDesc);
      notify('Draft proposal generated by Gemini AI and cleaned.', 'success');
    } catch (error) {
        const message = error instanceof Error ? error.message : "AI generation failed.";
        notify(message, 'error');
    }
    setIsGenerating(false);
  };

  const totalPercentage = milestones.reduce((sum, m) => sum + Number(m.percentage), 0);

  const handleUserSelect = (user: User | null) => {
    setSelectedUser(user);
  };

  const handleMilestoneChange = (index: number, field: keyof Milestone, value: string | number) => {
    const newMilestones = [...milestones];
    if (field === 'percentage') {
        newMilestones[index] = { ...newMilestones[index], [field]: parseFloat(value as string) || 0 };
    } else {
        newMilestones[index] = { ...newMilestones[index], [field]: value };
    }
    setMilestones(newMilestones);
  };

  const addMilestone = () => {
    setMilestones([...milestones, { id: `m${Date.now()}`, task: '', percentage: 0, status: 'Pending', deadline: new Date().toISOString().split('T')[0] }]);
  };

  const removeMilestone = (index: number) => {
    const newMilestones = milestones.filter((_, i) => i !== index);
    setMilestones(newMilestones);
  };

  const handleCreateProposal = async () => {
    if (!user) {
        notify('You must be logged in to submit a proposal.', 'error');
        return;
    }

    if (!isFormValid) {
        notify('Please fill all required fields: Proposal Name, Recipient, and Budget.', 'warn');
        return;
    }
    
    if (!selectedUser || !selectedUser.id) {
        notify('Please select a recipient for the proposal.', 'warn');
        return;
    }

    if (isUrgent && (!eventDate || !venue)) {
      notify('For urgent bookings, please provide both an Event Date and Venue.', 'warn');
      return;
    }

    const contentToCheck = `${eventName} ${genre} ${description} ${receiverNote} ${milestones.map((m) => m.task).join(' ')}`;
    
    if (checkContentForViolation(contentToCheck)) {
      onBlockUser();
      notify(MODERATION_WARNING_TEXT, 'error');
      return;
    }

    if (paymentType === 'Milestone Based' && totalPercentage !== 100) {
      notify('Total milestone percentage must equal 100%.', 'warn');
      return;
    }

    const contractContent = `// DRAFT CONTRACT for ${eventName}\n// Recipient: ${selectedUser.name}\n// Budget: ${rate} ${currency}\n\n${description}`;

    const normalizedPaymentType = paymentType === 'Lump Sum' ? 'Lump Sum' : 'Milestone Based';
    
    const proposalPayload: any = {
      eventName: eventName,
      artistId: selectedUser.id,
      artistName: selectedUser.name,
      budget: rate,
      currency: currency,
      paymentType: normalizedPaymentType,
      milestones: paymentType === 'Milestone Based' ? milestones.map(({ id, ...m }) => m) : [],
      description: description,
      contractContent: contractContent,
      proposerId: user.id,
      isUrgent: isUrgent,
      receiverNote: receiverNote,
    };

    if (isUrgent) {
      proposalPayload.eventDate = new Date(eventDate).toISOString();
      proposalPayload.venue = venue;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/proposals/collab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(proposalPayload),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create collab proposal. See server logs for details.');
      }
      
      const successMessage = isUrgent 
          ? 'Urgent proposal sent for admin review!' 
          : 'Draft saved! You and the recipient can now negotiate in the "Manage & Fund" tab.';

      notify(successMessage, 'success');
      await fetchProposals(true, new AbortController().signal); // Use a new signal for this specific fetch
      setActiveTab('manage');

      // Reset form fields
      setEventName('');
      setRate('');
      setGenre('');
      setDescription('');
      setSelectedUser(null);
      setIsUrgent(false);
      setEventDate('');
      setVenue('');
      setReceiverNote('');
      setMilestones([
        { id: 'm1', task: 'Initial Deposit', percentage: 50, status: 'Pending', deadline: new Date().toISOString().split('T')[0] },
        { id: 'm2', task: 'Project Completion', percentage: 50, status: 'Pending', deadline: new Date().toISOString().split('T')[0] },
      ]);

    } catch (error) {
      console.error("Error sending proposal:", error);
      notify((error as Error).message, 'error');
    }
  };

  const handleOpenSubmitWorkModal = (proposal: CollabProposal) => {
    setSelectedProposalForWork(proposal);
    setIsSubmitWorkModalOpen(true);
  };

  const handleOpenWorkReviewModal = (proposal: CollabProposal) => {
    setSelectedProposalForWork(proposal);
    setIsWorkReviewModalOpen(true);
  };

  // SURGICAL FIX: This function now immediately updates the UI with the response from the server.
  const handleProposalAction = async (proposalId: string, action: string, body: any = {}) => {
    if (action === 'negotiate') {
      const proposalToNegotiate = myProposals.find(p => p.id === proposalId);
      if (proposalToNegotiate) {
        handleOpenNegotiationModal(proposalToNegotiate);
      }
      return;
    }

    if (!token) {
      notify('Authentication token is missing. Please log in again.', 'error');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/proposals/collab/${proposalId}/${action}`.toLowerCase(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || `Failed to perform action: ${action}`);
      }

      // CORRECTED: Immediately update the local state with the server's response to prevent race conditions.
      // The server response is inconsistent; sometimes it's the proposal, sometimes it's { message, proposal }.
      const updatedProposal = responseData.proposal || responseData;

      if (updatedProposal && updatedProposal.id) {
        setMyProposals(prevProposals =>
          prevProposals.map(p =>
            p.id === updatedProposal.id ? updatedProposal : p
          )
        );
      } else {
        // Fallback to a full re-fetch if the proposal object isn't in the response.
        await fetchProposals(true, new AbortController().signal);
      }

      notify(responseData.message || `Action '${action}' completed successfully!`, 'success');

    } catch (error) {
      console.error(`Error performing action ${action} on proposal ${proposalId}:`, error);
      notify((error as Error).message, 'error');
      throw error; // Re-throw for calling components to handle (e.g., closing a modal).
    }
  };

  const handleSubmitWork = async (proposalId: string, submittedWorkLink: string, workDescription: string) => {
    await handleProposalAction(proposalId, 'fulfill', { submittedWorkLink, workDescription });
    // The modal will now close automatically via the useEffect hook
    // setIsSubmitWorkModalOpen(false);
  };


  const handleFundEscrow = (proposal: CollabProposal) => {
    if (!token) {
      notify('Please log in before funding a proposal.', 'error');
      return;
    }
    setProposalToFund(proposal);
    setShowPayment(true);
  };

  const handleDraftChange = (updates: Partial<CollabProposal>) => {
    setDraftProposal(prev => prev ? { ...prev, ...updates } : null);
  };

  const handleOpenNegotiationModal = (proposal: CollabProposal) => {
    const initialDraft = {
        ...proposal,
        description: proposal.description || proposal.contractData?.content || '',
    };
    setSelectedProposalForNegotiation(proposal);
    setDraftProposal(initialDraft);
    
    const isProposer = user?.id === proposal.proposerId;
    const previousVersion = prevMyProposals.find(p => p.id === proposal.id);
    setPreviousProposalForDiff(previousVersion || null);

    const hasChangesForRecipient = !isProposer && previousVersion && 
        (proposal.budget !== previousVersion.budget || 
         (proposal.description || proposal.contractData?.content) !== (previousVersion.description || previousVersion.contractData?.content));

    let initialViewMode: ViewMode = 'chat';
    if (isProposer) {
        initialViewMode = 'edit';
    } else if (hasChangesForRecipient) {
        initialViewMode = 'diff';
    }

    setNegotiationViewMode(initialViewMode);
    setIsNegotiationModalOpen(true);
  };

  const handleViewContract = (proposal: CollabProposal) => {
    handleOpenNegotiationModal(proposal);
  };

  const handleCloseNegotiationModal = () => {
      setIsNegotiationModalOpen(false);
      setSelectedProposalForNegotiation(null);
      setDraftProposal(null);
  }
  
  const handleUpdateContract = (content: string) => {
    if (!currentProposalId) return;

    setMyProposals((prev) =>
      prev.map((p) => {
        if (p.id === currentProposalId && p.contractData) {
          return { ...p, contractData: { ...p.contractData, content, lastEditedBy: user?.name || 'User', version: p.contractData.version + 1 } };
        }
        return p;
      })
    );
    notify('Contract draft updated locally.', 'info');
  };

  const executeProposalUpdate = async (proposalId: string, updates: Partial<CollabProposal>) => {
    if (!token) {
      notify('Authentication token is missing. Please log in again.', 'error');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/proposals/collab/${proposalId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(updates),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update proposal');
      }
      notify('Proposal updated successfully!', 'success');
      await fetchProposals(true, new AbortController().signal); // Use a new signal for this specific fetch
      setIsNegotiationModalOpen(false);
    } catch (error) {
      console.error('Error updating proposal:', error);
      notify((error as Error).message, 'error');
    }
  };

  const handleUpdateProposal = async () => {
    if (!draftProposal || !selectedProposalForNegotiation) return;

    const updates: Partial<CollabProposal> = {};
    if (draftProposal.budget !== selectedProposalForNegotiation.budget) {
        updates.budget = draftProposal.budget;
    }
    if (draftProposal.description !== (selectedProposalForNegotiation.description || selectedProposalForNegotiation.contractData?.content)) {
        updates.description = draftProposal.description;
    }

    if (Object.keys(updates).length === 0) {
        notify('No changes to submit.', 'info');
        return;
    }

    const originalBudget = parseFloat(selectedProposalForNegotiation.budget || '0');
    const newBudget = parseFloat(draftProposal.budget || '0');
    const budgetIncreased = newBudget > originalBudget;

    if (budgetIncreased && user) {
        const amountDifference = newBudget - originalBudget;
        setProposalUpdatePayload({ id: selectedProposalForNegotiation.id, updates });
        const newTransaction: Transaction = {
            id: `temp-tx-${Date.now()}`,
            userId: user.id,
            type: 'funding_increase',
            status: 'pending',
            amount: amountDifference,
            currency: selectedProposalForNegotiation.currency,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            details: { eventName: selectedProposalForNegotiation.eventName, proposalId: selectedProposalForNegotiation.id },
        };
        setTransactionForUpdate(newTransaction);
        setIsDualPaymentModalOpen(true); 
    } else {
        await executeProposalUpdate(selectedProposalForNegotiation.id, updates);
    }
  };

  const handleSendMessage = async (proposalId: string, messageText: string) => {
    if (!token || !user || !selectedProposalForNegotiation) {
      notify('Cannot send message.', 'error');
      return;
    }

    const newMessage: Message = {
        id: `temp_${Date.now()}`,
        text: messageText,
        senderId: user.id,
        timestamp: new Date().toISOString(),
    };

    setDraftProposal(prev => prev ? ({ ...prev, chatHistory: [...(prev.chatHistory || []), newMessage] }) : null);

    try {
        const response = await fetch(`${API_BASE_URL}/api/proposals/collab/${proposalId}/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ message: messageText }),
        });
        if (!response.ok) throw new Error('Failed to send message.');
    } catch (error) {
        notify((error as Error).message, 'error');
        fetchProposals(false, new AbortController().signal); // Use a new signal for this specific fetch
    }
  };

  const handleContractStatusChange = (status: 'Active' | 'Negotiation' | 'Rejected', notes?: string) => {
    if (!currentProposalId) return;
    setMyProposals((prev) =>
      prev.map((p) => {
        if (p.id === currentProposalId && p.contractData) {
          const newStatus = status === 'Active' ? 'Accepted' : status === 'Negotiation' ? 'Negotiation' : 'Rejected';
          return { ...p, status: newStatus as ProposalStatus, contractData: { ...p.contractData, status, adminNotes: notes } };
        }
        return p;
      })
    );
    notify(`Contract status changed to ${status}`, 'info');
  };

  const isFormValid = eventName && selectedUser && selectedUser.id && rate;

  const sortedProposals = [...myProposals].sort(
    (a, b) => Number(b.isUrgent) - Number(a.isUrgent)
  );

  const transactionForModal: Transaction | null = isFormValid && user ? {
    id: `temp-tx-${Date.now()}`,
    userId: user.id,
    type: 'funding',
    status: 'pending',
    amount: parseFloat(rate),
    currency: currency,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    details: { eventName: eventName },
  } : null;

  useEffect(() => {
    if (selectedProposalForNegotiation) {
      const updatedProposal = myProposals.find(p => p.id === selectedProposalForNegotiation.id);
      if (updatedProposal && JSON.stringify(updatedProposal) !== JSON.stringify(selectedProposalForNegotiation)) {
        setSelectedProposalForNegotiation(updatedProposal);
      }
    }
  }, [myProposals]);


  useEffect(() => {
    if (selectedProposalForWork) {
        const updatedProposal = myProposals.find(p => p.id === selectedProposalForWork.id);
        if (updatedProposal && JSON.stringify(updatedProposal) !== JSON.stringify(selectedProposalForWork)) {
            setSelectedProposalForWork(updatedProposal);

            // If the work submission modal is open and the proposal is now fulfilled, close it.
            if (isSubmitWorkModalOpen && updatedProposal.status === ProposalStatus.FULFILLED) {
              setIsSubmitWorkModalOpen(false);
            }
        }
    }
  }, [myProposals, isSubmitWorkModalOpen]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Briefcase className="text-kala-secondary" /> Proposals & Contracts
          </h2>
          <p className="text-kala-400 text-sm">
            Create, negotiate, and fund proposals for any user on the portal.
          </p>
        </div>

        <div className="flex bg-kala-800 p-1 rounded-lg border border-kala-700">
          <button
            onClick={() => setActiveTab('manage')}
            className={`px-4 py-2 rounded-md text-sm font-bold transition-all flex items-center gap-2 ${
              activeTab === 'manage'
                ? 'bg-kala-secondary text-kala-900 shadow-lg'
                : 'text-kala-400 hover:text-white'
            }`}
          >
            <FileText className="w-4 h-4" /> Manage & Fund
            {myProposals.some(
              (p) =>
                p.status === 'Accepted' ||
                p.status === 'Negotiation' ||
                p.isUrgent
            ) && (
              <span
                className={`w-2 h-2 rounded-full ${myProposals.some((p) => p.isUrgent) ? 'bg-red-500 animate-pulse' : 'bg-yellow-500'}`}
              ></span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`px-4 py-2 rounded-md text-sm font-bold transition-all flex items-center gap-2 ${
              activeTab === 'create'
                ? 'bg-kala-secondary text-kala-900 shadow-lg'
                : 'text-kala-400 hover:text-white'
            }`}
          >
            <Send className="w-4 h-4" /> New Proposal
          </button>
        </div>
      </div>

      {activeTab === 'create' ? (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 animate-in fade-in">
          <div className="xl:col-span-2 bg-kala-800/50 backdrop-blur-md border border-kala-700 rounded-xl p-8">
            <div className="space-y-8">
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                <p className="text-xs text-red-200">
                  {MODERATION_WARNING_TEXT}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-bold text-kala-500 uppercase tracking-wider mb-4 border-b border-kala-700 pb-2">
                  1. Proposal Details
                </h3>
                <div className="mb-4">
                  <label className="block text-sm text-kala-300 mb-1 font-medium">
                    Proposal / Project Name
                  </label>
                  <input
                    type="text"
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                    className="w-full bg-kala-900 border border-kala-700 rounded-lg px-4 py-2.5 text-white focus:ring-1 focus:ring-kala-secondary outline-none"
                    placeholder="e.g. Album Art Design Contract"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-kala-300 mb-1 font-medium">
                      Recipient (Service Provider)
                    </label>
                    <UserSearch onSelectUser={handleUserSelect} selectedUserName={selectedUser?.name} />
                  </div>
                  <div>
                    <label className="block text-sm text-kala-300 mb-1 font-medium">
                      Category / Theme
                    </label>
                    <input
                      type="text"
                      value={genre}
                      onChange={(e) => setGenre(e.target.value)}
                      className="w-full bg-kala-900 border border-kala-700 rounded-lg px-4 py-2.5 text-white focus:ring-1 focus:ring-kala-secondary outline-none"
                      placeholder="e.g. Graphic Design"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm text-kala-300 mb-1 font-medium">
                    Note for Recipient (Optional)
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
                    <input
                      type="text"
                      value={receiverNote}
                      onChange={(e) => setReceiverNote(e.target.value)}
                      className="w-full bg-kala-900 border-kala-700 rounded-lg pl-10 pr-4 py-2.5 text-white focus:ring-1 focus:ring-kala-secondary outline-none"
                      placeholder="e.g. Loved your portfolio, let's collaborate!"
                    />
                  </div>
                </div>
                <div className="mt-4 flex items-center">
                  <input
                    type="checkbox"
                    id="isUrgent"
                    checked={isUrgent}
                    onChange={(e) => setIsUrgent(e.target.checked)}
                    className="h-4 w-4 rounded border-kala-600 bg-kala-800 text-kala-secondary focus:ring-kala-secondary"
                  />
                  <label htmlFor="isUrgent" className="ml-2 text-sm font-medium text-kala-300">
                    Mark as Urgent (requires specific date & location if applicable)
                  </label>
                </div>

                {isUrgent && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 p-4 bg-red-900/20 border border-red-500/30 rounded-lg animate-in fade-in">
                        <div>
                            <label className="block text-sm text-red-300 mb-1 font-medium flex items-center gap-2"><Calendar className="w-4 h-4"/>Target Date</label>
                            <input
                                type="date"
                                value={eventDate}
                                onChange={(e) => setEventDate(e.target.value)}
                                className="w-full bg-kala-900 border-kala-700 rounded px-2 py-1.5 text-sm mt-1 focus:ring-1 focus:ring-kala-secondary outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-red-300 mb-1 font-medium flex items-center gap-2"><MapPin className="w-4 h-4"/>Location/Venue</label>
                            <input
                                type="text"
                                value={venue}
                                onChange={(e) => setVenue(e.target.value)}
                                placeholder="e.g. Remote / Online"
                                className="w-full bg-kala-900 border-kala-700 rounded px-2 py-1.5 text-sm mt-1 focus:ring-1 focus:ring-kala-secondary outline-none"
                            />
                        </div>
                    </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-bold text-kala-500 uppercase tracking-wider mb-4 border-b border-kala-700 pb-2">
                  2. Scope & Payment
                </h3>
                <div className="mb-4">
                  <label className="block text-sm text-kala-300 mb-1 flex justify-between">
                    <span>
                      Scope of Work / Proposal Draft
                    </span>
                    <button
                      onClick={handleAIGenerate}
                      disabled={isGenerating || !selectedUser || !eventName}
                      className="text-xs text-kala-secondary hover:text-purple-400 flex items-center gap-1 transition-colors disabled:opacity-50"
                    >
                      {isGenerating ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Sparkles className="w-3 h-3" />
                      )}
                      {isGenerating
                        ? 'Generating...'
                        : 'Auto-Generate with Gemini'}
                    </button>
                  </label>
                  <textarea
                    rows={12}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full bg-kala-900 border border-kala-700 rounded-lg px-4 py-2 text-white focus:ring-1 focus:ring-kala-secondary outline-none resize-y font-mono text-sm"
                    placeholder="Clearly define the work to be done, deliverables, and timelines. Or, click Auto-Generate to create a detailed draft."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                  <div className="relative">
                    <label className="block text-sm text-kala-300 mb-1 font-medium">
                      Budget / Rate
                    </label>
                    <DollarSign className="absolute left-3 top-9 w-4 h-4 text-kala-500" />
                    <input
                      type="number"
                      value={rate}
                      onChange={(e) => setRate(e.target.value)}
                      className="w-full bg-kala-900 border border-kala-700 rounded-lg pl-10 pr-4 py-2.5 text-white focus:ring-1 focus:ring-kala-secondary outline-none"
                      placeholder="1000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-kala-300 mb-1 font-medium">
                      Currency
                    </label>
                    <div className="relative">
                      <Coins className="absolute left-3 top-9 w-4 h-4 text-kala-500" />
                      <select
                        value={currency}
                        onChange={(e) =>
                          setCurrency(e.target.value as 'USD' | 'ETH' | 'KALA')
                        }
                        className="w-full bg-kala-900 border border-kala-700 rounded-lg pl-10 pr-4 py-2.5 text-white focus:ring-1 focus:ring-kala-secondary outline-none appearance-none"
                      >
                        <option value="USD">USD (Fiat)</option>
                        <option value="ETH">ETH (Crypto)</option>
                        <option value="KALA">KALA (DAO Token)</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-kala-300 mb-1 font-medium">
                      Payout Mode
                    </label>
                    <select
                      value={paymentType}
                      onChange={(e) =>
                        setPaymentType(
                          e.target.value as 'Lump Sum' | 'Milestone Based'
                        )
                      }
                      className="w-full bg-kala-900 border border-kala-700 rounded-lg px-4 py-2.5 text-white focus:ring-1 focus:ring-kala-secondary outline-none appearance-none"
                    >
                      <option value="Lump Sum">Lump Sum Escrow</option>
                      <option value="Milestone Based">Milestone Based</option>
                    </select>
                  </div>
                </div>

                {paymentType === 'Milestone Based' && (
                  <div className="space-y-4 pt-4 mt-4 border-t border-kala-700">
                    <div className="flex justify-between items-center">
                      <h4 className="text-md font-semibold text-kala-200">Define Milestones</h4>
                      <button onClick={addMilestone} className="text-sm text-kala-secondary font-bold flex items-center gap-1">
                        <Plus className="w-4 h-4"/> Add Milestone
                      </button>
                    </div>
                    {milestones.map((milestone, index) => (
                      <div key={milestone.id} className="grid grid-cols-12 gap-3 items-center bg-kala-900/50 p-3 rounded-lg">
                        <div className="col-span-4">
                          <label className="text-xs text-kala-400">Task for Recipient</label>
                          <input
                            type="text"
                            value={milestone.task}
                            onChange={(e) => handleMilestoneChange(index, 'task', e.target.value)}
                            className="w-full bg-kala-800 border-kala-700 rounded px-2 py-1.5 text-sm mt-1 focus:ring-1 focus:ring-kala-secondary outline-none"
                            placeholder={`Milestone ${index + 1}`}
                          />
                        </div>
                        <div className="col-span-3">
                          <label className="text-xs text-kala-400">Payout %</label>
                          <input
                            type="number"
                            value={milestone.percentage}
                            onChange={(e) => handleMilestoneChange(index, 'percentage', e.target.value)}
                            className="w-full bg-kala-800 border-kala-700 rounded px-2 py-1.5 text-sm mt-1 focus:ring-1 focus:ring-kala-secondary outline-none"
                            placeholder="e.g. 50"
                          />
                        </div>
                        <div className="col-span-4">
                          <label className="text-xs text-kala-400">Deadline</label>
                          <input
                            type="date"
                            value={milestone.deadline || ''}
                            onChange={(e) => handleMilestoneChange(index, 'deadline', e.target.value)}
                            className="w-full bg-kala-800 border-kala-700 rounded px-2 py-1.5 text-sm mt-1 focus:ring-1 focus:ring-kala-secondary outline-none"
                          />
                        </div>
                        <div className="col-span-1 flex items-end">
                          <button onClick={() => removeMilestone(index)} className="text-red-500 hover:text-red-400 p-2 rounded-full bg-kala-800 mt-5">
                            <Trash2 className="w-4 h-4"/>
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-end items-center mt-2">
                        <div className={`text-sm font-bold ${totalPercentage === 100 ? 'text-green-500' : 'text-red-500'}`}>
                            Total: {totalPercentage}%
                        </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center mt-6">
                   <button
                        onClick={() => {
                            if (isFormValid) {
                                setShowDualPayment(true);
                            } else {
                                notify('Please fill name, recipient, and budget to see payment options.', 'warn');
                            }
                        }}
                        className="text-xs text-kala-secondary hover:text-white font-bold flex items-center gap-1 bg-kala-900 px-3 py-1.5 rounded border border-kala-700 hover:border-kala-500 transition-colors"
                    >
                        <RefreshCw className="w-3 h-3" /> Payment Options
                    </button>
                  {onOpenExchange && (
                    <button
                      onClick={onOpenExchange}
                      className="text-xs text-kala-secondary hover:text-white font-bold flex items-center gap-1 bg-kala-900 px-3 py-1.5 rounded border border-kala-700 hover:border-kala-500 transition-colors"
                    >
                      <RefreshCw className="w-3 h-3" /> Need to swap for{' '}
                      {currency}?
                    </button>
                  )}
                </div>
              </div>

              <div className="pt-8 mt-8 border-t border-kala-700">
                <p className="text-xs text-kala-400 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {isUrgent 
                    ? "Urgent proposals are sent directly for admin review and funded upon approval."
                    : "Standard proposals are saved as drafts first, allowing for negotiation before final submission."
                  }
                </p>
                <button
                  onClick={handleCreateProposal}
                  disabled={!isFormValid}
                  className={`w-full bg-gradient-to-r text-white font-bold py-4 rounded-xl hover:opacity-90 transition-opacity shadow-lg text-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${isUrgent ? 'from-red-600 to-rose-700' : 'from-kala-secondary to-purple-600'}`}
                >
                  <Send className="w-5 h-5" />
                  {isUrgent ? "Send Urgent Proposal for Review" : "Save as Draft & Begin Negotiation"}
                </button>
              </div>
            </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-indigo-900/50 to-kala-900 border border-indigo-700/30 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">
                Smart Contract Flow
              </h3>
              <ul className="text-sm text-indigo-200/80 space-y-3">
                <li className="flex gap-2">
                  <span className="bg-indigo-500/20 text-indigo-400 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold">
                    1
                  </span>
                  <span>
                    <b>Draft & Negotiate:</b> Standard proposals are saved as drafts for negotiation.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="bg-indigo-500/20 text-indigo-400 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold">
                    2
                  </span>
                  <span>
                    <b>Submit:</b> Once agreed, the proposer submits the draft for admin review.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="bg-indigo-500/20 text-indigo-400 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold">
                    3
                  </span>
                  <span>
                    <b>Urgent (Optional):</b> Urgent proposals skip drafting and go straight to admin review.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="bg-indigo-500/20 text-indigo-400 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold">
                    4
                  </span>
                  <span>
                    <b>Approve & Fund:</b> Admin approval triggers automatic escrow funding.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="bg-indigo-500/20 text-indigo-400 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold">
                    5
                  </span>
                  <span><b>Execute:</b> Work begins after the recipient accepts the active proposal.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      ) : (
        <div className="space-y-6 animate-in slide-in-from-right-4 fade-in">
          {sortedProposals.map((prop) => (
            <ProposalProgressBar
              key={prop.id}
              proposal={prop}
              onAction={handleProposalAction}
              onFund={handleFundEscrow}
              onViewContract={handleViewContract}
              onNegotiate={handleOpenNegotiationModal}
              onSubmitWork={handleOpenSubmitWorkModal}
              onReviewWork={handleOpenWorkReviewModal}
            />
          ))}
        </div>
      )}

      {showPayment && proposalToFund && user && (
        <PaymentGateway
          amount={proposalToFund.budget}
          currency={proposalToFund.currency || 'USD'}
          itemDescription={`Escrow Funding for ${proposalToFund.eventName}`}
          onSuccess={(method) => {
            // This needs to be a proper API call
            notify(`Escrow Funded via ${method}! Smart Contract Deployed.`,'success');
            setShowPayment(false);
            setProposalToFund(null);
          }}
          onCancel={() => {
            setShowPayment(false);
            setProposalToFund(null);
          }}
          token={token}
		  userId={user.id}
        />
      )}

      {showDualPayment && transactionForModal && (
        <DualPaymentModal
          isOpen={showDualPayment}
          onClose={() => setShowDualPayment(false)}
          transaction={transactionForModal}
          onSuccess={() => {
            notify('Payment action completed!', 'success');
            setShowDualPayment(false);
          }}
          onCancel={() => setShowDualPayment(false)}
          serviceName={transactionForModal.details.eventName}
          token={token}
        />
      )}

      {isDualPaymentModalOpen && transactionForUpdate && (
        <DualPaymentModal
            isOpen={isDualPaymentModalOpen}
            onClose={() => setIsDualPaymentModalOpen(false)}
            transaction={transactionForUpdate}
            onSuccess={() => {
                if (proposalUpdatePayload) {
                    executeProposalUpdate(proposalUpdatePayload.id, proposalUpdatePayload.updates);
                }
                setIsDualPaymentModalOpen(false);
            }}
            token={token}
        />
      )}

      {isEditorOpen && viewingContract && (
        <ContractEditor
          contract={viewingContract}
          userRole={user?.role || UserRole.ARTIST} 
          isReadOnly={false}
          onClose={() => setIsEditorOpen(false)}
          onSave={handleUpdateContract}
          onStatusChange={handleContractStatusChange}
          onBlockUser={onBlockUser}
        />
      )}

      {isNegotiationModalOpen && selectedProposalForNegotiation && (
          <CollabNegotiationModal
              key={selectedProposalForNegotiation.id}
              isOpen={isNegotiationModalOpen && !isDualPaymentModalOpen}
              viewMode={negotiationViewMode}
              proposal={selectedProposalForNegotiation}
              draftProposal={draftProposal}
              previousProposal={previousProposalForDiff}
              currentUser={user}
              onClose={handleCloseNegotiationModal}
              onViewModeChange={setNegotiationViewMode}
              onDraftChange={handleDraftChange}
              onUpdate={handleUpdateProposal}
              onSendMessage={handleSendMessage}
              isReadOnly={isNegotiationModalReadOnly}
          />
      )}

      {isSubmitWorkModalOpen && selectedProposalForWork && (
        <SubmitWorkModal
          isOpen={isSubmitWorkModalOpen}
          onClose={() => setIsSubmitWorkModalOpen(false)}
          proposal={selectedProposalForWork}
          onSubmit={handleSubmitWork}
          onBlockUser={onBlockUser}
        />
      )}

      {isWorkReviewModalOpen && selectedProposalForWork && (
          <WorkReviewModal
              isOpen={isWorkReviewModalOpen}
              onClose={() => setIsWorkReviewModalOpen(false)}
              proposal={selectedProposalForWork}
              onApprove={() => handleProposalAction(selectedProposalForWork.id, 'release')}
          />
      )}

    </div>
  );
};

export default BookingHub;
